import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PentestReport, PentestReportsFilters } from './types';
import { getPentestReports } from '../../utils/api/endpoints/automated-report/automated-report';
import useUserCredentials from '../../utils/hooks/user/useUserCredentials';
import { UserRole } from '../../utils/api/endpoints/user/credentials';
import FindingsReportIcon from '../../assets/icons/findings_report.svg';
import ViewDetailsReportIcon from '../../assets/icons/view_details_report.svg';
import PentestReportIcon from '../../assets/icons/pentest_report_icon.svg';
import { pdf, Document } from '@react-pdf/renderer';
import LetterOfAttestationPDF from './components/sections/LetterOfAttestationPDF';
import { isAttestationEnabled } from './utils/attestationUtils';
import { getProgramReportById } from '../../utils/api/endpoints/program-reports/program-reports';

const PentestReports: React.FC = () => {
  const navigate = useNavigate();
  const { role } = useUserCredentials();
  const [reports, setReports] = useState<PentestReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    limit: 4
  });

  const [filters, setFilters] = useState<PentestReportsFilters>({
    page: 1,
    limit: 4
  });

  // Dynamic status options
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loaDownloadingId, setLoaDownloadingId] = useState<string | number | null>(null);

  const handleDownloadLoaCard = async (report: PentestReport) => {
    if (loaDownloadingId) return;
    try {
      setLoaDownloadingId(report.id);
      const res = await getProgramReportById(String(report.id));
      const fullReportData = res?.data ?? res;
      const doc = (
        <Document>
          <LetterOfAttestationPDF reportData={fullReportData} />
        </Document>
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (report.company_name || 'Client').replace(/[^a-zA-Z0-9_-]/g, '_');
      link.download = `${safeName}-Letter-of-Attestation.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading LOA:', err);
      alert('Could not generate Letter of Attestation. Please click "View Details" to preview and download.');
    } finally {
      setLoaDownloadingId(null);
    }
  };

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.status) count++;
    if (filters.company_name) count++;
    if (filters.date_from || filters.date_to) count++;
    return count;
  };

  const filteredReports = useMemo(() => {
    if (!searchQuery.trim()) return reports;
    const term = searchQuery.toLowerCase();
    return reports.filter(report => {
      const programValues = report.program_names
        ? Object.values(report.program_names).join(' ').toLowerCase()
        : '';
      return (
        report.title?.toLowerCase().includes(term) ||
        report.company_name?.toLowerCase().includes(term) ||
        report.document_number?.toLowerCase().includes(term) ||
        programValues.includes(term)
      );
    });
  }, [reports, searchQuery]);

  // Filter reports based on user role and status
  const getVisibleReports = (allReports: PentestReport[]): PentestReport[] => {
    if (!allReports) return [];

    switch (role) {
      case UserRole.QA:
        return allReports.filter(report =>
          [
            'draft',
            'qa_review',
            'business_requested_changes',
            'rejected'
          ].includes(report.status)
        );
      case UserRole.ADMIN:
      case UserRole.SUB_ADMIN:
      case UserRole.ADMIN_MANAGER:
        return allReports.filter(report =>
          [
            'qa_review',
            'admin_review',
            'approved',
            'rejected',
            'business_review',
            'business_requested_changes',
            'changes_added',
            'report_updated',
            'business_approved'
          ].includes(report.status)
        );
      default:
        return allReports;
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPentestReports(filters);
      if (response.status === 'success') {
        const visibleReports = response.data.reports;
        setReports(visibleReports);
        setPagination(response.data.pagination);
        // Extract unique statuses from visible reports
        const uniqueStatuses = Array.from(
          new Set(visibleReports.map((r: any) => r.status).filter(Boolean))
        ) as string[];
        setAvailableStatuses(uniqueStatuses);
      } else {
        setError(response.message || 'Failed to fetch reports');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [filters, role]);

  const handleFilterChange = (newFilters: Partial<PentestReportsFilters>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const clearAllFilters = () => {
    setFilters(prev => ({
      ...prev,
      status: undefined,
      company_name: undefined,
      date_from: undefined,
      date_to: undefined,
      sort_by: undefined,
      sort_order: undefined,
      page: 1
    }));
    setSearchQuery('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'text-slate-700';
      case 'qa_review':
        return 'text-amber-700';
      case 'admin_review':
        return 'text-blue-800';
      case 'approved':
        return 'text-emerald-800';
      case 'rejected':
        return 'text-red-800';
      case 'business_review':
        return 'text-indigo-800';
      case 'business_requested_changes':
        return 'text-pink-700';
      case 'changes_added':
        return 'text-orange-700';
      case 'report_updated':
        return 'text-cyan-800';
      case 'business_approved':
        return 'text-purple-800';
      default:
        return 'text-slate-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
      case 'qa_review':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'admin_review':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      case 'approved':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'rejected':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'qa_review':
        return 'QA Review';
      case 'admin_review':
        return 'Admin Review';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'business_review':
        return 'Business Review';
      case 'business_requested_changes':
        return 'Business Requested Changes';
      case 'changes_added':
        return 'Changes Added (QA)';
      case 'report_updated':
        return 'Report Updated (Admin)';
      case 'business_approved':
        return 'Business Approved';
      default:
        return status;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'text-red-600';
      case 'high': return 'text-[#F97316]';
      case 'medium': return 'text-[#EAB308]';
      case 'low': return 'text-[#22C55E]';
      case 'info': return 'text-[#64748B]';
      default: return 'text-slate-500';
    }
  };

  const activeFiltersCount = getActiveFiltersCount() + (searchQuery.trim() ? 1 : 0);

  const pageItems = useMemo(() => {
    const totalPages = Math.max(1, pagination.total_pages || 1);
    const currentPage = Math.min(Math.max(1, pagination.current_page || 1), totalPages);
    const maxMiddlePages = 3;
    const items: Array<number | 'left-ellipsis' | 'right-ellipsis'> = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) items.push(i);
      return items;
    }

    items.push(1);
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 2) {
      start = 2;
      end = start + maxMiddlePages - 1;
    } else if (currentPage >= totalPages - 1) {
      end = totalPages - 1;
      start = end - maxMiddlePages + 1;
    }

    if (start > 2) items.push('left-ellipsis');

    for (let i = start; i <= end; i++) {
      if (i > 1 && i < totalPages) items.push(i);
    }

    if (end < totalPages - 1) items.push('right-ellipsis');
    items.push(totalPages);

    return items;
  }, [pagination.current_page, pagination.total_pages]);

  if (loading && reports.length === 0) {
    return (
      <div className="min-h-screen bg-[#F4F4F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-10 bg-slate-200 rounded-lg w-1/3 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className="p-6">
                    <div className="h-6 bg-slate-200 rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-slate-200 rounded w-1/2 mb-3"></div>
                    <div className="h-4 bg-slate-200 rounded w-2/3 mb-4"></div>
                    <div className="space-y-2">
                      <div className="h-3 bg-slate-200 rounded w-full"></div>
                      <div className="h-3 bg-slate-200 rounded w-3/4"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#010D2C] rounded-2xl flex items-center justify-center">
                <img src={PentestReportIcon} alt="Pentest report icon" className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-1">
                  Pentest Reports
                </h1>
                <p className="text-slate-600 text-sm">
                  Comprehensive security assessment reports and analytics
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-8">
          <div className="bg-white border border-slate-200 rounded-[12px] shadow-sm px-6 py-5">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <div className="relative">
                  <svg className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search"
                    className="w-full bg-white border border-slate-200 rounded-2xl pl-10 pr-4 py-3 text-sm text-slate-700 placeholder-slate-400 focus:ring-2 focus:ring-[#010D2C]/20 focus:border-[#010D2C] transition-all duration-200"
                  />
                </div>
              </div>

              {/* Status */}
              <div>
                <div className="relative">
                  <select
                    value={filters.status || ''}
                    onChange={e => handleFilterChange({ status: e.target.value || undefined })}
                    className="w-full appearance-none bg-white border border-slate-200 rounded-2xl px-4 py-3 pr-10 text-sm text-slate-700 focus:ring-2 focus:ring-[#010D2C]/20 focus:border-[#010D2C] transition-all duration-200"
                  >
                    <option value="">Status</option>
                    {availableStatuses.map(status => (
                      <option key={status} value={status}>
                        {getStatusText(status)}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Sort */}
              <div>
                <div className="relative">
                  <select
                    value={filters.sort_by && filters.sort_order ? `${filters.sort_by}|${filters.sort_order}` : ''}
                    onChange={e => {
                      const [sort_by, sort_order] = e.target.value.split('|');
                      handleFilterChange({
                        sort_by: sort_by as PentestReportsFilters['sort_by'],
                        sort_order: (sort_order as 'ASC' | 'DESC') || 'DESC'
                      });
                    }}
                    className="w-full appearance-none bg-white border border-slate-200 rounded-2xl px-4 py-3 pr-10 text-sm text-slate-700 focus:ring-2 focus:ring-[#010D2C]/20 focus:border-[#010D2C] transition-all duration-200"
                  >
                    <option value="" disabled>
                      Sort by
                    </option>
                    <option value="createdAt|DESC">Created Date (Newest)</option>
                    <option value="createdAt|ASC">Created Date (Oldest)</option>
                    <option value="updatedAt|DESC">Updated Date (Newest)</option>
                    <option value="updatedAt|ASC">Updated Date (Oldest)</option>
                    <option value="company_name|ASC">Company Name (A-Z)</option>
                    <option value="company_name|DESC">Company Name (Z-A)</option>
                    <option value="status|ASC">Status (A-Z)</option>
                    <option value="status|DESC">Status (Z-A)</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Date */}
              <div>
                <div className="relative">
                  <input
                    type="date"
                    placeholder="dd/mm/yyyy"
                    value={filters.date_from || ''}
                    onChange={e => {
                      const value = e.target.value || undefined;
                      handleFilterChange({
                        date_from: value,
                        date_to: value
                      });
                    }}
                    className="w-full appearance-none bg-white border border-slate-200 rounded-2xl pl-4 pr-4 py-3 text-sm text-slate-700 focus:ring-2 focus:ring-[#010D2C]/20 focus:border-[#010D2C] transition-all duration-200"
                  />
                </div>
              </div>
            </div>

            {activeFiltersCount > 0 && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearAllFilters}
                  className="text-sm font-medium text-[#010D2C] hover:text-[#0b1f4a] transition"
                >
                  Clear filters ({activeFiltersCount})
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-800 font-medium text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Reports Grid */}
        {filteredReports.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {filteredReports.map((report) => (
                <div
                  key={report.id}
                  className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden flex flex-col h-full min-h-[430px]"
                >
                  <div className="p-6 space-y-5 flex-1">
                    <div>
                      <h3 className="text-lg font-medium text-slate-900 leading-tight break-words">
                        {report.title}
                      </h3>
                      {/* Flow Badge for Admin Roles */}
                      {(role === UserRole.ADMIN || role === UserRole.SUB_ADMIN || role === UserRole.ADMIN_MANAGER || role === UserRole.QA) && (
                        <div className="mt-2">
                          {report.report_suffix ? (
                            <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-[11px] font-bold text-indigo-850 border border-indigo-200">
                              Combined / Scoped Report
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-bold text-slate-750 border border-slate-200">
                              Standard (Default) Report
                            </span>
                          )}
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-slate-600">
                        <span className="inline-flex items-center gap-2 text-slate-500">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {formatDate(report.created_at)}
                        </span>
                        <span className="inline-flex items-center gap-2 text-slate-500">
                          <img src="/icons/com_pen_rep.svg" alt="Company icon" className="w-4 h-4" />
                          {report.company_name}
                        </span>
                      </div>
                    </div>

                    <div className="bg-[#F9FAFB] border border-slate-200 rounded-lg px-4 py-4 space-y-1">
                      <p className="text-[11.05px] font-semibold text-slate-500 uppercase tracking-wide">
                        Document Number
                      </p>
                      <div className="text-xs font-mono text-slate-600 flex items-center gap-2">
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M7 7v10m10-10v10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {report.document_number}
                      </div>
                    </div>

                    {((report.program_names && Object.keys(report.program_names).length > 0) ||
                      (report.run_ids && Array.isArray(report.run_ids) && report.run_ids.length > 0) ||
                      report.run_id ||
                      (report.start_date && report.end_date)) && (
                      <div className="space-y-3">
                        {report.program_names && Object.keys(report.program_names).length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-slate-500 mb-2 tracking-wide">Program(s)</p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(report.program_names).map(([programId, programName]) => (
                                <span
                                  key={programId}
                                  className="inline-flex items-center px-3 py-2 rounded-lg bg-[#EFF6FF] text-[#1D4ED8] text-xs font-medium border border-[#BFDBFE]"
                                >
                                  {programName}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {((report.run_ids && Array.isArray(report.run_ids) && report.run_ids.length > 0) || report.run_id) && (
                          <div>
                            <p className="text-xs font-medium text-slate-500 mb-2 tracking-wide">Program Run(s)</p>
                            <div className="flex flex-wrap gap-2">
                              {report.run_ids && Array.isArray(report.run_ids) && report.run_ids.length > 0 ? (
                                report.run_ids.map((runId: number) => (
                                  <span
                                    key={runId}
                                    className="inline-flex items-center px-3 py-2 rounded-lg bg-orange-50 text-orange-700 text-xs font-medium border border-orange-200"
                                  >
                                    Run #{runId}
                                  </span>
                                ))
                              ) : (
                                <span className="inline-flex items-center px-3 py-2 rounded-lg bg-orange-50 text-orange-700 text-xs font-medium border border-orange-200">
                                  Run #{report.run_id}
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {report.start_date && report.end_date && (
                          <div className="rounded-lg bg-indigo-50/40 p-2.5 border border-indigo-100 text-[11px] text-gray-700">
                            <span className="font-bold text-indigo-850 uppercase tracking-wider block text-[9px] mb-0.5">Date Range Scoped:</span>
                            {formatDate(report.start_date)} - {formatDate(report.end_date)}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="h-px bg-slate-100" />

                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-[#374151]">
                        <img src={FindingsReportIcon} alt="Findings icon" className="w-4 h-4" />
                        Findings
                      </div>
                      <p className="text-xs text-slate-500 font-medium">
                        Version {report.current_version}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm w-full">
                      <div className="flex items-baseline gap-2">
                        <span className={`font-medium ${getSeverityColor('critical')}`}>Critical</span>
                        <span className="text-base font-medium text-slate-900">{report.critical_count}</span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className={`font-medium ${getSeverityColor('high')}`}>High</span>
                        <span className="text-base font-medium text-slate-900">{report.high_count}</span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className={`font-medium ${getSeverityColor('medium')}`}>Medium</span>
                        <span className="text-base font-medium text-slate-900">{report.medium_count}</span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className={`font-medium ${getSeverityColor('low')}`}>Low</span>
                        <span className="text-base font-medium text-slate-900">{report.low_count}</span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className={`font-medium ${getSeverityColor('info')}`}>Info</span>
                        <span className="text-base font-medium text-slate-900">{report.info_count || 0}</span>
                      </div>
                    </div>

                    <div className="h-px bg-slate-100" />

                    <div className="flex items-center justify-start -ml-1">
                      <span className={`inline-flex items-center gap-1.5 px-1 py-1 text-sm font-medium bg-transparent ${getStatusColor(report.status)}`}>
                        {getStatusIcon(report.status)}
                        {getStatusText(report.status)}
                      </span>
                    </div>
                  </div>

                  <div className="bg-[#010D2C] text-white px-6 py-4 flex items-center justify-between">
                    <p className="text-sm font-normal">
                      Total Findings: <span className="font-medium">{report.total_findings}</span>
                    </p>
                    <div className="flex items-center gap-3">
                      {isAttestationEnabled(report) && ['approved', 'report_updated', 'business_review', 'business_approved'].includes(report.status) && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-blue-600/30 hover:bg-blue-600/50 text-blue-200 border border-blue-400/30 transition disabled:opacity-50"
                          onClick={() => handleDownloadLoaCard(report)}
                          disabled={loaDownloadingId === report.id}
                          title="Download official Letter of Attestation PDF"
                        >
                          {loaDownloadingId === report.id ? (
                            <span>Generating...</span>
                          ) : (
                            <>
                              <svg className="w-3.5 h-3.5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span>Download LOA</span>
                            </>
                          )}
                        </button>
                      )}
                      <button
                        className="inline-flex items-center gap-2 text-sm font-medium text-[#58CC02] hover:text-emerald-200 transition"
                        onClick={() => navigate(`/dashboard/pentest-reports/${report.id}/edit`)}
                      >
                        View Details
                        <img src={ViewDetailsReportIcon} alt="View details icon" className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="mt-10 flex flex-col items-center gap-4">
              <p className="text-sm text-slate-500">
                Showing{' '}
                <span className="font-semibold text-slate-700">
                  {Math.min((pagination.current_page - 1) * pagination.limit + 1, pagination.total_count || 0)}
                </span>
                {' '}to{' '}
                <span className="font-semibold text-slate-700">
                  {Math.min(pagination.current_page * pagination.limit, pagination.total_count || 0)}
                </span>
                {' '}of{' '}
                <span className="font-semibold text-slate-700">
                  {pagination.total_count || 0}
                </span>{' '}
                results
              </p>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => handlePageChange(Math.max(1, pagination.current_page - 1))}
                  disabled={pagination.current_page <= 1}
                  className="w-10 h-10 rounded-[14px] border border-slate-200 bg-white text-slate-600 flex items-center justify-center hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <div className="flex items-center gap-2">
                  {pageItems.map((item, index) => {
                    if (item === 'left-ellipsis' || item === 'right-ellipsis') {
                      return (
                        <span key={`${item}-${index}`} className="px-2 text-slate-400 select-none">
                          …
                        </span>
                      );
                    }

                    const isActive = item === pagination.current_page;

                    return (
                      <button
                        key={item}
                        onClick={() => handlePageChange(item)}
                        className={`min-w-[42px] h-10 rounded-[14px] px-3 text-sm font-semibold transition ${isActive
                            ? 'bg-[#010D2C] text-white border border-[#010D2C] shadow-sm'
                            : 'bg-white border border-slate-200 text-slate-700 hover:text-[#010D2C] hover:border-[#010D2C]/60'
                          }`}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(Math.min(pagination.total_pages, pagination.current_page + 1))}
                  disabled={pagination.current_page >= pagination.total_pages}
                  className="w-10 h-10 rounded-[14px] border border-slate-200 bg-white text-slate-600 flex items-center justify-center hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </>
        ) : searchQuery.trim() ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <div className="text-slate-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">No reports match your search</h3>
            <p className="text-slate-600 mb-4 max-w-md mx-auto">Try searching by report name, company, document ID, or program.</p>
            <button
              onClick={() => setSearchQuery('')}
              className="text-sm font-medium text-[#010D2C] hover:text-[#0b1f4a] transition"
            >
              Clear search
            </button>
          </div>
        ) : (
          role === UserRole.BUSINESS ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
              <div className="text-blue-400 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">No reports found</h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">You haven't requested any pentest reports yet. Follow these steps to request your first report:</p>
              <ol className="text-left max-w-lg mx-auto space-y-4">
                <li className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold">1</span>
                  <span>Go to the <strong>Programs</strong> section using the side navigation bar.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold">2</span>
                  <span>Choose the program for which you want to request a report.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold">3</span>
                  <span>Click the <strong>Full Details</strong> button in the right-side Program Quick View component.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold">4</span>
                  <span>In the full view of the program, you will see the <strong>Request Report</strong> button.</span>
                </li>
              </ol>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
              <div className="text-slate-400 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">No reports found</h3>
              <p className="text-slate-600 mb-4 max-w-md mx-auto">Try adjusting your filters or navigate to a program to request a pentest report.</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default PentestReports; 